import React from "react";
import { useNavigate } from "react-router-dom";
import {
  DollarSign,
  Receipt,
  Landmark,
  BookOpen,
  Scale,
  Phone,
  Mail,
  FileText,
  Shield,
  Users,
  UserCog,
  Settings,
  ChevronRight,
  CreditCard,
  Crown,
  Palette,
  Building2,
  Globe,
  Hash,
  Pen,
  CalendarSync,
  Lock,
  Banknote,
  ArrowRightLeft,
  MessageSquare,
  Compass,
} from "lucide-react";
import { useUserDataContext } from "../../context/UserDataContext";
import { useGuidedTour } from "../../contexts/GuidedTourContext";

interface SettingsSection {
  id: string;
  label: string;
  description: string;
  icon: React.FC<any>;
  path: string;
  category: string;
  /** Runs instead of navigating to `path` */
  action?: "restartTour";
}

const sections: SettingsSection[] = [
  // Firm Profile
  {
    id: "branding",
    label: "Branding & Appearance",
    description: "Logo, colors, and visual identity",
    icon: Palette,
    path: "/settings/branding",
    category: "Firm Profile",
  },
  {
    id: "firm-profile",
    label: "Firm Details",
    description: "Name, address, jurisdiction, and contact info",
    icon: Building2,
    path: "/settings/firm-profile",
    category: "Firm Profile",
  },
  {
    id: "document-numbering",
    label: "Document Numbering",
    description: "Matter, invoice, and receipt prefixes",
    icon: Hash,
    path: "/settings/document-numbering",
    category: "Firm Profile",
  },

  // Subscription
  {
    id: "subscription",
    label: "Subscription & Billing",
    description: "Manage your plan, billing, and payment methods",
    icon: Crown,
    path: "/settings/subscription",
    category: "Subscription",
  },

  // Team
  {
    id: "users",
    label: "User Management",
    description: "Invite and manage team members",
    icon: Users,
    path: "/settings/users",
    category: "Team",
  },
  {
    id: "roles",
    label: "Role Management",
    description: "Define roles and permissions",
    icon: UserCog,
    path: "/settings/roles",
    category: "Team",
  },

  // Billing & Finance
  {
    id: "rate-tables",
    label: "Rate Tables",
    description: "Manage billing rate tables for your firm",
    icon: DollarSign,
    path: "/settings/rate-tables",
    category: "Billing & Finance",
  },
  {
    id: "fee-items",
    label: "Fee Items",
    description: "Standard fee items and disbursement catalog",
    icon: Receipt,
    path: "/settings/fee-items",
    category: "Billing & Finance",
  },
  {
    id: "bank-accounts",
    label: "Bank Accounts",
    description: "Trust and general bank account management",
    icon: Landmark,
    path: "/settings/bank-accounts",
    category: "Billing & Finance",
  },
  {
    id: "chart-of-accounts",
    label: "Chart of Accounts",
    description: "Manage your accounting chart of accounts",
    icon: BookOpen,
    path: "/settings/chart-of-accounts",
    category: "Billing & Finance",
  },
  {
    id: "tax-settings",
    label: "Tax Settings",
    description: "Configure HST, GST, PST rates and tax registration",
    icon: Receipt,
    path: "/settings/tax",
    category: "Billing & Finance",
  },
  {
    id: "reconciliation",
    label: "Reconciliation",
    description: "Bank reconciliation and statement matching",
    icon: Scale,
    path: "/settings/reconciliation",
    category: "Billing & Finance",
  },

  // Payments
  {
    id: "stripe-connect",
    label: "Payment Processing",
    description: "Connect Stripe to accept client payments",
    icon: CreditCard,
    path: "/settings/stripe-connect",
    category: "Payments",
  },
  {
    id: "payment-methods",
    label: "Payment Methods",
    description: "Configure accepted payment methods and instructions",
    icon: Banknote,
    path: "/settings/payment-methods",
    category: "Payments",
  },

  // Communication
  {
    id: "phone-numbers",
    label: "Phone Numbers",
    description: "Manage firm phone numbers and routing",
    icon: Phone,
    path: "/settings/phone-numbers",
    category: "Communication",
  },
  {
    id: "email-connections",
    label: "Email Connections",
    description: "Connect and manage email accounts",
    icon: Mail,
    path: "/settings/email-connections",
    category: "Communication",
  },
  {
    id: "email-signature",
    label: "Email Signature",
    description: "Customize your outgoing email signature",
    icon: Pen,
    path: "/settings/email-signature",
    category: "Communication",
  },
  {
    id: "calendar-sync",
    label: "Calendar Sync",
    description: "Connect Google or Outlook so events stay in sync",
    icon: CalendarSync,
    path: "/calendar",
    category: "Communication",
  },
  {
    id: "port-guide",
    label: "Number Porting Guide",
    description: "How to port your firm's phone number to Kavoa",
    icon: ArrowRightLeft,
    path: "/settings/port-guide",
    category: "Communication",
  },
  {
    id: "message-templates",
    label: "Messages & Notifications",
    description: "Customize message templates and reminder schedules",
    icon: MessageSquare,
    path: "/settings/message-templates",
    category: "Communication",
  },

  // Intake & Forms
  {
    id: "intake-forms",
    label: "Intake Forms",
    description: "Build and manage client intake forms",
    icon: FileText,
    path: "/settings/intake-forms",
    category: "Intake & Forms",
  },

  // Website & Widgets
  {
    id: "widgets",
    label: "Website Widgets",
    description: "Embed booking, form, and review widgets on your website",
    icon: Globe,
    path: "/settings/widgets",
    category: "Website & Widgets",
  },

  // Security & Audit
  {
    id: "audit-log",
    label: "Audit Log",
    description: "View all actions and changes in your firm",
    icon: Shield,
    path: "/settings/audit-log",
    category: "Security & Audit",
  },

  // Help
  {
    id: "restart-tour",
    label: "Restart Guided Tour",
    description: "Walk through creating your first matter again",
    icon: Compass,
    path: "",
    category: "Help",
    action: "restartTour",
  },
];

// Group sections by category
const categories = Array.from(new Set(sections.map((s) => s.category)));

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { canManageBilling } = useUserDataContext();
  const { restartTour } = useGuidedTour();

  const openSection = (section: SettingsSection) => {
    if (section.action === "restartTour") {
      restartTour();
      return;
    }
    navigate(section.path);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your firm's configuration and preferences</p>
      </div>

      {/* Settings Grid by Category */}
      <div className="space-y-8">
        {categories.map((category) => {
          const categoryItems = sections.filter((s) => s.category === category);
          return (
            <div key={category}>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                {category}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryItems.map((section) => {
                  const Icon = section.icon;
                  const isBillingRestricted = section.id === "subscription" && !canManageBilling;
                  return (
                    <div
                      key={section.id}
                      onClick={isBillingRestricted ? undefined : () => openSection(section)}
                      className={`bg-white rounded-xl shadow-sm border border-slate-200 p-5 transition-all duration-200 group ${
                        isBillingRestricted
                          ? "opacity-60 cursor-not-allowed"
                          : "cursor-pointer hover:shadow-md hover:border-brand-light"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div
                            className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: "var(--color-primary-light)" }}
                          >
                            <Icon className="h-5 w-5" style={{ color: "var(--color-primary)" }} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800 group-hover:text-brand transition-colors">
                              {section.label}
                            </h3>
                            <p className="text-sm text-slate-500 mt-0.5">
                              {isBillingRestricted
                                ? "Contact your administrator to manage billing"
                                : section.description}
                            </p>
                          </div>
                        </div>
                        {isBillingRestricted ? (
                          <Lock className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-brand-accent transition-colors flex-shrink-0 mt-0.5" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SettingsPage;
